import { useMemo, useCallback } from 'react';

import { Link } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { Column } from 'react-table';
import { useQueryParams } from 'use-query-params';

import { fetchUsers } from 'api';
import { LinkButton } from 'components';
import { Table } from 'components/Table';
import { colors } from 'config';
import { getPageCount, convertUTCDateTimeToDateTime } from 'helpers';
import { StringParam, BooleanParam } from 'helpers/useQueryParams';
import { useUrlPagination, useControlledPagination } from 'hooks';
import { RoleEnum, UserProps } from 'types';

import { DashboardWrapper, CellWrapper } from '../styledComponents';
import { UserTableSidebar } from './UserTableSidebar';

const filterConfig = {
  organization_name: StringParam,
  email: StringParam,
  first_name: StringParam,
  last_name: StringParam,
  last_login_min: StringParam,
  last_login_max: StringParam,
  is_cognito: BooleanParam,
};

export const SystemAdminUserTable = () => {
  const { controlledRows, controlledPageIndex } = useUrlPagination();
  const location = useLocation();
  const urlFrom = useMemo(
    () => location.pathname + location.search,
    [location.pathname, location.search],
  );
  const [filters, setFilters] = useQueryParams(filterConfig, {
    updateType: 'replaceIn',
  });
  const config = useMemo(() => {
    const {
      organization_name,
      email,
      first_name,
      last_name,
      last_login_max,
      last_login_min,
      is_cognito,
    } = filters;
    return {
      params: {
        page_number: controlledPageIndex,
        limit: controlledRows,
        first_name: first_name,
        last_name: last_name,
        organization_name: organization_name,
        last_login_min: last_login_min,
        last_login_max: last_login_max,
        email: email,
        is_cognito: is_cognito,
        role: [
          'signer',
          'auditor',
          'candidate_admin',
          'system_admin',
        ] as Array<RoleEnum>,
        fuzzy: true,
      },
    };
  }, [controlledPageIndex, controlledRows, filters]);
  const { dataArray, dataLoaded, count } = useControlledPagination(
    fetchUsers,
    config,
  );
  const { updateControlledPageIndex } = useUrlPagination();

  // update and return to first page when filter is applied
  const wrapSetCandidateFilter = useCallback(
    (arg: Parameters<typeof setFilters>[0]) => {
      setFilters(arg);
      updateControlledPageIndex(1);
    },
    [setFilters, updateControlledPageIndex],
  );
  const columns = useMemo(
    (): Array<Column<UserProps>> => [
      {
        id: 'edit',
        Header: () => {
          return (
            <CellWrapper aria-hidden>
              <p aria-label='Edit' />
            </CellWrapper>
          );
        },

        Cell: (row: any) => {
          const { is_cognito } = row.row.original;
          return (
            <CellWrapper>
              <LinkButton
                variant='outlined'
                sx={{ width: '70%' }}
                to={{
                  pathname: `users/${row.row.original.id}`,
                  state: { from: urlFrom },
                }}
                disabled={is_cognito}
                aria-label={
                  'Edit User ' +
                  row.row.original.first_name +
                  ' ' +
                  row.row.original.last_name
                }
              >
                EDIT
              </LinkButton>
            </CellWrapper>
          );
        },
      },
      {
        id: 'first_name',
        Header: 'First Name',
        accessor: 'first_name',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'last_name',
        Header: 'Last Name',
        accessor: 'last_name',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'role',
        Header: 'Role',
        accessor: 'role',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'email',
        Header: 'Email',
        accessor: 'email',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'organization',
        Header: 'Organization',
        accessor: (row) => {
          const { organization } = row;

          return organization ? (
            <Link variant='body1' href={`organizations/${organization.id}`}>
              {organization.name}
            </Link>
          ) : (
            ''
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'status',
        Header: 'Status',
        accessor: 'account_status',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'last_login',
        Header: 'Last Login',
        accessor: (row) => {
          const { last_login } = row;

          return last_login ? convertUTCDateTimeToDateTime(last_login) : '';
        },
        disableFilters: true,
        disableSortBy: false,
      },
    ],
    [urlFrom],
  );
  return (
    <DashboardWrapper>
      <>
        <UserTableSidebar
          setFilters={wrapSetCandidateFilter}
          filters={filters}
        />
        <Table<UserProps>
          count={count}
          loading={!dataLoaded}
          rowProps={(row) => {
            return !row.is_cognito
              ? {
                  style: {
                    backgroundColor: colors.uswds.orange10,
                    borderBottom: '1px solid white',
                  },
                }
              : {};
          }}
          useTableOptions={{
            data: dataArray,
            columns: columns,
            manualPagination: true,
            pageCount: getPageCount(count, controlledRows),
            autoResetPage: false,

            // defaultCanSort: false,
            // defaultCanFilter: false,
            // canFilter: false,
            // canSort:false,
            disableSort: true,
          }}
        />
      </>
    </DashboardWrapper>
  );
};
